/**
 * qualify-popup-trigger.js
 * ============================================================================
 * Generic trigger handler for qualify-popup section modals.
 *
 * Usage — add a single attribute on any clickable element:
 *   <a data-qualify-popup="psi-careprogram:open">Terms</a>
 *   <button data-qualify-popup="lottery-rules:open">Rules</button>
 *
 * The value is the CustomEvent name that the matching qualify-popup block
 * listens for. The trigger element dispatches that event on `document` and
 * the section opens the corresponding modal.
 *
 * Handles: click + keyboard (Enter / Space) + aria-expanded toggle. Uses
 * event delegation, so it works for elements added to the DOM after page
 * load (e.g. dynamically injected HTML sections).
 *
 * NOTE: This is the NEW pattern. The legacy `data-apl-rules` +
 * `data-apl-open-event` markup (see sections/fall-sale-card--box.liquid)
 * is still supported via its own per-section JS handler and is intentionally
 * left untouched for backward compatibility.
 */
(function () {
  // Idempotency guard: Shopify may render <script> tags multiple times when
  // a section is included in several places on the same page.
  if (window.__qualifyPopupTriggerLoaded) return;
  window.__qualifyPopupTriggerLoaded = true;

  // aria-expanded token — prevents stale 400ms callbacks from clobbering
  // the state set by a subsequent rapid click.
  function flashExpanded(trigger) {
    trigger.setAttribute('aria-expanded', 'true');
    var token = (trigger.__qltoken = (trigger.__qltoken || 0) + 1);
    setTimeout(function () {
      if (trigger.__qltoken === token) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    }, 400);
  }

  function dispatch(trigger) {
    var eventName = trigger.getAttribute('data-qualify-popup');
    if (!eventName) return false;
    document.dispatchEvent(
      new CustomEvent(eventName, { detail: { trigger: trigger } })
    );
    flashExpanded(trigger);
    return true;
  }

  // Click delegation
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-qualify-popup]');
    if (!trigger) return;
    if (trigger.tagName === 'A') e.preventDefault();
    dispatch(trigger);
  });

  // Keyboard delegation (Enter / Space) — for non-button elements that
  // don't natively activate on keyboard input.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var target = e.target;
    // Don't intercept keystrokes the user is actively typing
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) return;
    // Browsers natively fire click on Enter for <a>/<button> and on Space
    // for <button>. Our click handler already covers those — skip keydown
    // here to avoid double-dispatching the popup event.
    if (e.key === 'Enter' && (target.tagName === 'A' || target.tagName === 'BUTTON')) return;
    if (e.key === ' ' && target.tagName === 'BUTTON') return;
    var trigger = target.closest('[data-qualify-popup]');
    if (!trigger) return;
    e.preventDefault();
    dispatch(trigger);
  });
})();